import { z } from 'zod';
/**
 * Content contracts (92 §3.2 "Content types"). The zod schemas are the
 * runtime contract for JSON under `content/`.
 */
import { NEED_IDS } from '../design/needs';
export const NeedIdSchema = z.enum(NEED_IDS);
const id = z.string().min(1);
const text = z.string().min(1);
/** Partial map of need deltas, e.g. {"energy": -3, "hygiene": -5}. */
const needDeltas = z.partialRecord(NeedIdSchema, z.number());
export const ChoiceOptionSchema = z.strictObject({
    label: text,
    outcome: text,
    effects: z.strictObject({
        /** Whole naira (Q-03). */
        naira: z.number().int().optional(),
        need: needDeltas.optional(),
        /** Stall reputation delta. */
        stars: z.union([z.literal(-1), z.literal(1)]).optional(),
    }),
});
export const ChoiceCardSchema = z.strictObject({
    id,
    context: z.enum(['akara', 'street']),
    trigger: z.enum(['any', 'outage', 'rush']),
    text,
    options: z.array(ChoiceOptionSchema).min(2).max(3),
    gloss: text.optional(),
    igbo: z.boolean(),
});
export const ChipSchema = z.strictObject({
    id,
    text,
    gloss: text.optional(),
    igbo: z.boolean(),
});
export const ItemDefSchema = z.strictObject({
    id,
    /** Small integer used on the wire (Q-21 `look`). */
    netId: z.number().int().min(0),
    name: text,
    footprint: z.strictObject({ w: z.number().int().min(1), h: z.number().int().min(1) }),
    placement: z.enum(['floor', 'wall', 'outdoor']),
    price: z.number().int().min(0),
    /** Tiles relative to the footprint's top corner where a character uses the item. */
    useSpots: z.array(z.strictObject({ x: z.number().int(), y: z.number().int() })),
    effects: needDeltas,
    blurbId: id,
});
/** LL-38 place template. */
export const PlaceTemplateSchema = z.strictObject({
    poiId: id,
    sign: text,
    roles: z.array(text),
    vibes: z.array(z.strictObject({ text, icon: text })),
    outcomes: z.record(z.string(), text),
});
/** LL-41 mood label. */
export const MoodLabelSchema = z.strictObject({
    need: NeedIdSchema,
    band: z.enum(['low', 'ok', 'high']),
    label: text,
});
/** LL-36 placement error codes. */
export const PLACEMENT_ERROR_CODES = [
    'door_blocked',
    'build_limit',
    'out_of_bounds',
    'overlap',
    'wall_only',
    'floor_only',
    'outdoor_only',
    'not_enough_naira',
    'not_your_room',
    'item_unknown',
    'cannot_rotate',
    'path_blocked',
];
export const PlacementErrorCodeSchema = z.enum(PLACEMENT_ERROR_CODES);
export const PlacementErrorSchema = z.strictObject({
    code: PlacementErrorCodeSchema,
    text,
});
/** Validators for whole content files (arrays with unique ids/codes). */
const unique = (keys) => new Set(keys).size === keys.length;
export const ChoiceCardFileSchema = z.array(ChoiceCardSchema).refine((a) => unique(a.map((c) => c.id)), 'duplicate card id');
export const ChipFileSchema = z.array(ChipSchema).refine((a) => unique(a.map((c) => c.id)), 'duplicate chip id');
export const ItemDefFileSchema = z.array(ItemDefSchema).refine((a) => unique(a.map((c) => c.id)), 'duplicate item id');
export const PlaceTemplateFileSchema = z.array(PlaceTemplateSchema).refine((a) => unique(a.map((c) => c.poiId)), 'duplicate poiId');
export const MoodLabelFileSchema = z.array(MoodLabelSchema).refine((a) => unique(a.map((m) => `${m.need}/${m.band}`)), 'duplicate need/band');
export const PlacementErrorFileSchema = z.array(PlacementErrorSchema).refine((a) => unique(a.map((e) => e.code)), 'duplicate error code');
